import { ViewportObserver } from "./viewport";

export interface Bounds {
  readonly height: number;
  readonly width: number;
  readonly top: number;
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
}

export interface CullingObserverEntry {
  boundingClientRect: Bounds;
  intersectionRect: Bounds;
  intersectionRatio: number;
  readonly target: Element;
}

export type CullingObserverCallback = (entries: CullingObserverEntry[]) => void;

export class CullingObserver {
  private readonly entries: CullingObserverEntry[] = [];
  private readonly callback: CullingObserverCallback;

  constructor(callback: CullingObserverCallback) {
    this.callback = callback;

    ViewportObserver.shared.listen(this.onViewportUpdate);
  }

  private intersectElement(element: Element) {
    const boundingClientRect = ViewportObserver.shared.getBoundingClientRect(element);

    const a = boundingClientRect;

    const viewWidth = ViewportObserver.shared.viewWidth;
    const viewHeight = ViewportObserver.shared.viewHeight;
    const b = {
      left: 0,
      top: 0 - viewHeight * 0.5,
      width: viewWidth,
      height: viewHeight * 2,
    };

    const x0 = Math.max(a.left, b.left);
    const x1 = Math.min(a.left + a.width, b.left + b.width);

    if (x0 < x1) {
      const y0 = Math.max(a.top, b.top);
      const y1 = Math.min(a.top + a.height, b.top + b.height);

      if (y0 < y1) {
        const intWidth = x1 - x0;
        const intHeight = y1 - y0;

        const intersectionRect = {
          height: intHeight,
          width: intWidth,
          top: y0,
          left: x0,
          right: x1,
          bottom: y1,
        };

        const origArea = a.width * a.height;
        const intArea = intWidth * intHeight;

        return {
          boundingClientRect,
          intersectionRect,
          intersectionRatio: origArea / intArea,
        };
      }
    }

    const intersectionRect = {
      height: 0,
      width: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };

    return {
      boundingClientRect,
      intersectionRect,
      intersectionRatio: 0,
    };
  }

  private onViewportUpdate = (scrollY: number, scrollX: number, viewHeight: number, viewWidth: number) => {
    for (let entry of this.entries) {
      const updated = this.intersectElement(entry.target);

      entry.boundingClientRect = updated.boundingClientRect;
      entry.intersectionRect = updated.intersectionRect;
      entry.intersectionRatio = updated.intersectionRatio;
    }

    this.callback(this.entries);
  };

  public observe(element: HTMLElement): void {
    const intersectedElement = this.intersectElement(element);

    const entry = {
      ...intersectedElement,
      target: element,
    };

    this.entries.push(entry);
    this.callback([entry]);
  }

  public unobserve(element: HTMLElement): void {
    for (let i = 0, n = this.entries.length; i < n; ++i) {
      const entry = this.entries[i];
      if (entry.target === element) {
        this.entries.splice(i, 1);
        return;
      }
    }
  }

  public disconnect(): void {
    ViewportObserver.shared.unlisten(this.onViewportUpdate);
  }
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/culling.ts